import { createApi } from '@reduxjs/toolkit/query/react'
import { createAuthBaseQuery } from './base-query'
import { SCRIPT_API } from '@/config/constants'
import {
  CompileScriptResponseSchema,
  EvaluateScriptResponseSchema,
  ListScriptVersionsResponseSchema,
  ListScriptsResponseSchema,
  PutScriptResponseSchema,
  UserScriptSchema,
  type CompileScriptResponse,
  type EvaluateScriptRequest,
  type EvaluateScriptResponse,
  type PutScriptRequest,
  type PutScriptResponse,
  type ScriptVersion,
  type UserScript,
  type UserScriptSummary,
} from '@/types/schemas/edgescript'

/**
 * RTK Query slice for edge-script-api (the EdgeScript service, phase 2 plan Task 2.0).
 * Auth: access token, the same Cognito Bearer every edge service validates.
 *
 * `evaluateScript` is a query, not a mutation, because the server answer is a pure
 * function of its arguments (the service caches on the same key), so RTK's arg-keyed
 * cache gives sibling charts on one ticker a free second read. The script indicator
 * hook drives it lazily and keeps the per-bar values in its own map; see
 * `features/edgescript/chart/use-script-indicators.ts`.
 *
 * This module imports nothing from the language package so the slice stays loadable
 * in user builds with the feature flag folded off.
 */
export const edgeScriptApi = createApi({
  reducerPath: 'edgeScriptApi',
  baseQuery: createAuthBaseQuery(SCRIPT_API, 'access'),
  tagTypes: ['UserScript'],
  endpoints: (builder) => ({
    evaluateScript: builder.query<EvaluateScriptResponse, EvaluateScriptRequest>({
      query: (body) => ({ url: '/v1/edgescript/evaluate', method: 'POST', body }),
      transformResponse: (raw) => EvaluateScriptResponseSchema.parse(raw),
      // Retry-After rides beside the error, not inside `data`: a 503 IDENTITY_UNRESOLVED
      // body is plain text, which spreading into an object would mangle. The chart retries
      // a busy evaluator once after it (chart/evaluate-error.ts).
      transformErrorResponse: (error, meta) => {
        const retryAfter = (meta as { response?: Response } | undefined)?.response?.headers.get(
          'Retry-After',
        )
        return retryAfter ? { ...error, retryAfter } : error
      },
      // Bar-close refreshes re-request narrow tails with a moving window, so a
      // short retention keeps those entries from piling up.
      keepUnusedDataFor: 30,
    }),
    compileScript: builder.mutation<
      CompileScriptResponse,
      { source: string; target?: 'chart' | 'graph' | 'engine' }
    >({
      query: (body) => ({ url: '/v1/edgescript/compile', method: 'POST', body }),
      transformResponse: (raw) => CompileScriptResponseSchema.parse(raw),
    }),

    // ── My scripts (Task 2.4): per-user, the list never ships source ──
    listScripts: builder.query<UserScriptSummary[], void>({
      query: () => ({ url: '/v1/edgescript/scripts?limit=200' }),
      transformResponse: (raw) => ListScriptsResponseSchema.parse(raw).scripts,
      providesTags: (result) => [
        { type: 'UserScript', id: 'LIST' },
        ...(result ?? []).map((s) => ({ type: 'UserScript' as const, id: s.scriptId })),
      ],
    }),
    getScript: builder.query<UserScript, string>({
      query: (scriptId) => ({ url: `/v1/edgescript/scripts/${encodeURIComponent(scriptId)}` }),
      transformResponse: (raw) => UserScriptSchema.parse(raw),
      providesTags: (_r, _e, scriptId) => [{ type: 'UserScript', id: scriptId }],
    }),
    putScript: builder.mutation<PutScriptResponse, PutScriptRequest>({
      query: ({ scriptId, ...body }) => ({
        url: `/v1/edgescript/scripts/${encodeURIComponent(scriptId)}`,
        method: 'PUT',
        body,
      }),
      transformResponse: (raw) => PutScriptResponseSchema.parse(raw),
      invalidatesTags: (_r, _e, { scriptId }) => [
        { type: 'UserScript', id: 'LIST' },
        { type: 'UserScript', id: scriptId },
      ],
    }),
    deleteScript: builder.mutation<{ ok: boolean }, string>({
      query: (scriptId) => ({
        url: `/v1/edgescript/scripts/${encodeURIComponent(scriptId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, scriptId) => [
        { type: 'UserScript', id: 'LIST' },
        { type: 'UserScript', id: scriptId },
      ],
    }),
    listScriptVersions: builder.query<ScriptVersion[], string>({
      query: (scriptId) => ({
        url: `/v1/edgescript/scripts/${encodeURIComponent(scriptId)}/versions`,
      }),
      transformResponse: (raw) => ListScriptVersionsResponseSchema.parse(raw).versions,
      providesTags: (_r, _e, scriptId) => [{ type: 'UserScript', id: scriptId }],
    }),
  }),
})

export const {
  useLazyEvaluateScriptQuery,
  useEvaluateScriptQuery,
  useCompileScriptMutation,
  useListScriptsQuery,
  useGetScriptQuery,
  useLazyGetScriptQuery,
  usePutScriptMutation,
  useDeleteScriptMutation,
  useListScriptVersionsQuery,
  useLazyListScriptVersionsQuery,
} = edgeScriptApi
